import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { DollarSign, RefreshCw, Calculator, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const PortfolioDetails = ({
  tradingMode,
  displayPortfolio,
  coinbaseBalance,
  liveDataSource,
  syncing,
  fullSyncing,
  fullSyncStatus,
  syncWithExchange,
  startFullHistorySync,
  handleOpenCostBasis,
  fetchTrades,
}) => {
  return (
    <Card className="matrix-card rounded-none" data-testid="portfolio-details-card">
      <CardHeader className="border-b border-[#003B00] p-4">
        <CardTitle className="text-sm uppercase tracking-widest flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" strokeWidth={1.5} />
            {tradingMode === 'live' ? 'COINBASE BALANCE' : 'MULTI-COIN PORTFOLIO'}
          </div>
          <div className="flex flex-col gap-1">
            {tradingMode === 'live' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    coinbaseBalance?.success 
                      ? 'bg-green-900/50 text-green-400' 
                      : 'bg-yellow-900/50 text-yellow-400 animate-pulse'
                  }`}>
                    {coinbaseBalance?.success ? '● LIVE' : '○ LOADING'}
                  </span>
                </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={handleOpenCostBasis}
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                    data-testid="cost-basis-btn"
                    title="View cost basis for all holdings (FIFO)"
                  >
                    <Calculator className="h-3 w-3 mr-1" />
                    COST BASIS
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        toast.info('Calculating P/L for all sells...');
                        const [typesRes, plRes] = await Promise.all([
                          axios.post(`${API_V2}/user/trades/fix-types`, {}, { withCredentials: true }),
                          axios.post(`${API_V2}/user/trades/calculate-pl`, {}, { withCredentials: true })
                        ]);
                        toast.success(`Fixed ${typesRes.data.fixed_count || 0} types, calculated P/L for ${plRes.data.calculated || 0} sells`);
                        fetchTrades();
                      } catch (error) {
                        toast.error('Failed to calculate P/L');
                        console.error(error);
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs border-green-500 text-green-400 hover:bg-green-500/20"
                    data-testid="fix-pl-btn"
                    title="Calculate P/L for all sells using FIFO"
                  >
                    <TrendingUp className="h-3 w-3 mr-1" />
                    FIX P/L
                  </Button>
                </div>
          </div>
        </CardTitle>
        {fullSyncStatus && fullSyncing && (
          <div className="mt-2 p-2 bg-purple-900/20 border border-purple-500/30 rounded">
            <div className="flex justify-between text-xs text-purple-400 mb-1">
              <span>Syncing order history...</span>
              <span>{fullSyncStatus.progress || 0}%</span>
            </div>
            <div className="h-1 bg-purple-900/50 rounded overflow-hidden">
              <div 
                className="h-full bg-purple-500 transition-all duration-500"
                style={{ width: `${fullSyncStatus.progress || 0}%` }}
              />
            </div>
            <div className="text-xs text-purple-300 mt-1">
              {fullSyncStatus.orders_fetched || 0} orders fetched, {fullSyncStatus.orders_synced || 0} new orders synced
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-[#008F11] text-sm">USD BALANCE</span>
          <span className="font-bold" data-testid="usd-balance">
            ${(displayPortfolio?.usd_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <Separator className="bg-[#003B00]" />
        <div className="text-[#008F11] text-xs mb-1">COIN HOLDINGS</div>
        {[
          { label: 'BTC', key: 'btc_balance', testId: 'btc-balance' },
          { label: 'ETH', key: 'eth_balance', testId: 'eth-balance' },
          { label: 'SOL', key: 'sol_balance', testId: 'sol-balance' },
          { label: 'LTC', key: 'ltc_balance', testId: 'ltc-balance' },
          { label: 'DOGE', key: 'doge_balance', testId: 'doge-balance' },
          { label: 'BCH', key: 'bch_balance', testId: 'bch-balance' },
          { label: 'XRP', key: 'xrp_balance', testId: 'xrp-balance' },
          { label: 'SHIB', key: 'shib_balance', testId: 'shib-balance' },
        ].map(({ label, key, testId }) => (
          <div key={label} className="flex justify-between">
            <span className="text-[#008F11] text-sm">{label}</span>
            <span className="font-bold" data-testid={testId}>
              {displayPortfolio[key]?.toFixed(8) || '0.00000000'}
            </span>
          </div>
        ))}
        <Separator className="bg-[#003B00]" />
        <div className="flex justify-between">
          <span className="text-[#008F11] text-sm">TOTAL TRADES</span>
          <span className="font-bold" data-testid="total-trades">{displayPortfolio.total_trades}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#008F11] text-sm">WINNING TRADES</span>
          <span className="font-bold text-[#00FF41]" data-testid="winning-trades">{displayPortfolio.winning_trades}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#008F11] text-sm">WIN RATE</span>
          <span className="font-bold" data-testid="win-rate">{displayPortfolio.win_rate}%</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioDetails;
